// API client for the GDPR self-service endpoints (audit P1-8):
// workspace data export download and destructive workspace deletion.
import { ApiError, apiDelete, buildHeaders } from '../../../shared/api-client/index.js';

export type WorkspaceExportFormat = 'json' | 'csv';

export interface WorkspaceExportDownload {
  blob: Blob;
  fileName: string;
}

export interface DeleteWorkspaceDataInput {
  password: string;
  confirm: true;
}

/**
 * Downloads the workspace data export as a Blob (a file response, so it skips
 * the JSON-shaped apiGet helper). Filename carries the date, mirroring the
 * server's Content-Disposition.
 */
export async function downloadWorkspaceDataExport(
  format: WorkspaceExportFormat = 'json',
): Promise<WorkspaceExportDownload> {
  const res = await fetch(
    `/api/v1/workspaces/current/export?format=${encodeURIComponent(format)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: buildHeaders(),
    },
  );

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: unknown; message?: unknown; code?: string };
      const raw = body.message ?? body.error;
      if (typeof raw === 'string' && raw.trim() !== '') message = raw;
      if (typeof body.code === 'string') code = body.code;
    } catch {
      // Body is not JSON — keep the HTTP status message.
    }
    throw new ApiError(res.status, message, code);
  }

  return {
    blob: await res.blob(),
    fileName: `workspace-export-${new Date().toISOString().slice(0, 10)}.${
      format === 'csv' ? 'zip' : 'json'
    }`,
  };
}

/**
 * Destructive: deletes the whole workspace (owner-only, password-confirmed).
 * On success every member session — including the caller's — is revoked.
 */
export async function deleteWorkspaceData(input: DeleteWorkspaceDataInput): Promise<void> {
  await apiDelete('/workspaces/current', input);
}

/** Machine-readable refusal reason from the backend error envelope, if any. */
export function workspaceDeleteRefusalReason(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const details = err.details;
  if (typeof details !== 'object' || details === null) return undefined;
  const reason = (details as Record<string, unknown>)['reason'];
  return typeof reason === 'string' ? reason : undefined;
}
